import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { trpc } from '@org/clients/school';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Skeleton,
  useAuthorization,
} from '@org/ui';
import { Eye, X } from 'lucide-react';

const STORAGE_KEY = 'teacher-impersonation-id';

type TeacherContextValue = {
  selectedTeacherId: string | undefined;
  setSelectedTeacherId: (teacherId: string | undefined) => void;
  isImpersonating: boolean;
};

const TeacherContext = createContext<TeacherContextValue | null>(null);

/**
 * Provider component that manages teacher impersonation state.
 * Wrap your app with this to enable admin impersonation features.
 */
export function TeacherProvider({ children }: { children: React.ReactNode }) {
  const { hasPermission, isLoading: isPermissionLoading } = useAuthorization();
  const isAdmin = hasPermission('admin:all');

  // Initialize from localStorage
  const [selectedTeacherId, setSelectedTeacherIdState] = useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored || undefined;
  });

  // Clear selection if user is not an admin
  useEffect(() => {
    if (!isPermissionLoading && !isAdmin && selectedTeacherId) {
      setSelectedTeacherIdState(undefined);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isAdmin, isPermissionLoading, selectedTeacherId]);

  const setSelectedTeacherId = useCallback((teacherId: string | undefined) => {
    setSelectedTeacherIdState(teacherId);
    if (teacherId) {
      localStorage.setItem(STORAGE_KEY, teacherId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const isImpersonating = isAdmin && !!selectedTeacherId;

  const value = useMemo(
    () => ({
      selectedTeacherId: isAdmin ? selectedTeacherId : undefined,
      setSelectedTeacherId,
      isImpersonating,
    }),
    [selectedTeacherId, setSelectedTeacherId, isAdmin, isImpersonating],
  );

  return <TeacherContext.Provider value={value}>{children}</TeacherContext.Provider>;
}

function useTeacherContext(): TeacherContextValue {
  const context = useContext(TeacherContext);
  if (!context) {
    throw new Error('useTeacherContext must be used within a TeacherProvider');
  }
  return context;
}

/**
 * TeacherPicker component - allows admins to select which teacher's data to view.
 * Only visible to users with admin:all permission.
 * Stores selected teacherId in localStorage for persistence across navigations.
 */
export function TeacherPicker() {
  const { hasPermission, isLoading: isPermissionLoading } = useAuthorization();
  const isAdmin = hasPermission('admin:all');

  // Don't render anything for non-admins
  if (!isAdmin && !isPermissionLoading) {
    return null;
  }

  if (isPermissionLoading) {
    return null;
  }

  return <TeacherPickerContent />;
}

function TeacherPickerContent() {
  const { selectedTeacherId, setSelectedTeacherId } = useTeacherContext();
  const { data: teachers, isLoading } = trpc.users.getTeachers.useQuery();

  const selectedTeacher = useMemo(() => {
    if (!selectedTeacherId || !teachers) return null;
    return teachers.find((t) => t.userId === selectedTeacherId);
  }, [selectedTeacherId, teachers]);

  const handleTeacherChange = useCallback(
    (teacherId: string) => {
      if (teacherId === '__clear__') {
        setSelectedTeacherId(undefined);
      } else {
        setSelectedTeacherId(teacherId);
      }
    },
    [setSelectedTeacherId],
  );

  const handleClear = useCallback(() => {
    setSelectedTeacherId(undefined);
  }, [setSelectedTeacherId]);

  if (isLoading) {
    return <Skeleton className="h-9 w-48" />;
  }

  if (!teachers?.length) {
    return null;
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      {selectedTeacher ? (
        <Badge
          variant="outline"
          className="flex min-w-0 items-center gap-1.5 border-amber-200 bg-amber-50 px-2 py-1 text-amber-700"
        >
          <Eye className="h-3 w-3 shrink-0" />
          <span className="truncate text-xs">
            Viewing: {selectedTeacher.firstName} {selectedTeacher.lastName}
          </span>
          <button
            onClick={handleClear}
            className="ml-1 shrink-0 rounded-full p-0.5 hover:bg-amber-200"
            title="Clear selection"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ) : null}

      <Select value={selectedTeacherId ?? ''} onValueChange={handleTeacherChange}>
        <SelectTrigger className="w-48 min-w-0 border-rose-100 bg-white/80 text-sm">
          <SelectValue placeholder="Select a teacher..." />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {selectedTeacherId && (
            <SelectItem value="__clear__" className="text-gray-500">
              Clear selection
            </SelectItem>
          )}
          {teachers.map((teacher) => (
            <SelectItem key={teacher.userId} value={teacher.userId}>
              {teacher.firstName} {teacher.lastName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Hook to get the current teacherId for tRPC queries.
 * Returns the impersonated teacher's ID if admin is impersonating,
 * otherwise returns undefined (backend will use authenticated user's ID).
 */
export function useTeacherId(): string | undefined {
  const context = useContext(TeacherContext);
  // If used outside provider, return undefined (no impersonation)
  if (!context) {
    return undefined;
  }
  return context.selectedTeacherId;
}

/**
 * Hook to check if the current user is viewing as another teacher (admin impersonation).
 */
export function useIsImpersonating(): boolean {
  const context = useContext(TeacherContext);
  // If used outside provider, not impersonating
  if (!context) {
    return false;
  }
  return context.isImpersonating;
}
